import React from 'react';
import { FrameBone, ParagraphBone, RemoveFromWishlistButton, SvgIcon } from '@gik/ui';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { useApi } from '@gik/core/api';
import Tooltip from '@gik/ui/Tooltip';
import bemBlock from '@gik/core/utils/bemBlock';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserStore } from '@gik/core/store/UserStore';
import WishlistEmptyState from './WishlistEmptyState';
import { useWishlistProducts } from '@gik/api/inkinds/wishlistProducts';
import WishlistProductTile from './WishlistProductTile';
import SeeFullWishlistButton from './SeeFullWishlistButton';

export interface IWishlistRowsProps {
  inkindRouteId: string;
  skeleton?: boolean;
  skeletonItems?: number;
}

const skelBem = bemBlock(`wishlist-rows-skeleton`);
const Skeleton = ({ skeletonItems = 3 }: Partial<IWishlistRowsProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={20} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </div>
);

function WishlistRows({ inkindRouteId }: IWishlistRowsProps): React.ReactElement {
  const userStore = useUserStore();
  const userId = userStore.id;

  const { data: inkindPage, error: inkindPageError } = useApi<InkindPageAPIModel>(
    inkindRouteId ? `inkinds/${inkindRouteId}` : null
  );

  const {
    data: wishListProducts,
    isValidating: isLoadingWishlistProducts,
    error: wishListProductsError,
  } = useWishlistProducts(inkindRouteId);

  const canEdit = canUserEditPage(inkindPage, userId);

  const maxProductsToDisplay = 6;

  // if (!inkindPage && (inkindPageError || wishListProductsError)) {
  //   return <span className="gik-text-error">{getApiErrorString([inkindPageError, wishListProductsError])}</span>;
  // }

  const tiles = React.useMemo(() => {
    return wishListProducts?.slice(0, maxProductsToDisplay)?.map(item => {
      return (
        <WishlistProductTile
          key={item.id}
          item={item}
          inkindRouteId={inkindRouteId}
          direction="row"
          toolbar={
            <>
              {canEdit && (
                <RemoveFromWishlistButton
                  inkindRouteId={inkindRouteId}
                  itemId={item.id}
                  child={onClick => (
                    <Tooltip text="REMOVE PRODUCT">
                      <a
                        className={`gik-product-tile__close`}
                        onClick={(ev: React.MouseEvent<HTMLElement>) => {
                          ev.stopPropagation();
                          onClick(ev);
                        }}
                      >
                        <SvgIcon name="Trash" size="xs" set="hero-icons" />
                      </a>
                    </Tooltip>
                  )}
                />
              )}
            </>
          }
        />
      );
    });
  }, [inkindRouteId, canEdit, wishListProducts]);

  if (!wishListProducts || !inkindPage) {
    return <Skeleton />;
  }

  const hasItems = wishListProducts?.length > 0;
  const bem = bemBlock('wishlist-rows');

  const isLoading = !inkindPage || !wishListProducts;
  const isError = isLoading && (inkindPageError || wishListProductsError);

  if (isLoading) {
    return <Skeleton />;
  }
  if (isError) {
    return <Skeleton />;
  }

  return (
    <div className={bem()}>
      {hasItems && tiles}
      {wishListProducts?.length > maxProductsToDisplay && <SeeFullWishlistButton inkindPage={inkindPage} />}
      {!isLoadingWishlistProducts && !hasItems && <WishlistEmptyState inkindRouteId={inkindRouteId} proportion={1.1} />}
    </div>
  );
}

export default withSuspense(WishlistRows, Skeleton);
